// Census format transformers. Each transformer takes a parsed sheet (with
// headers + data row objects) and produces a normalized internal model:
//
//   {
//     employees: NormalizedEmployee[],
//     meta: {
//       sourceFormat,
//       sourceSheet,
//       hasEmployerTaxColumns,    // drives validation panel render
//       hasEarningsBreakdown,     // drives stability panel render
//       detectedPaySchedule,      // 'Weekly' | 'Biweekly' | ...
//       detectedSingleState,      // 'NY' if all rows share a state, else null
//       missingFields: string[],  // fields that were not found in source
//       existingDeductions: { majorMedical, dental, vision, retirement }
//     }
//   }
//
// NormalizedEmployee shape (compat with existing engine which expects `salary`
// and `filing`; we also write `annualSalary` + `filingStatus` for legacy reads):
//
//   {
//     employeeId, name, firstName, lastName, email,
//     salary, annualSalary,         // numbers (annual)
//     state, filing, filingStatus,
//     payFrequency, hireDate, weeklyHours, workerType, department,
//     filingStatusInferred,         // false until inference applied
//     sourceFormat, sourceSheet,
//     validation: { actualFutaEr, actualMedicareEr, actualSsEr, actualStateEr },
//     earnings: { regularPay, overtimePay, otRatio, otFlag, totalEarnings, variableIncomeFlag, regularPct },
//     existingDeductions: { majorMedical, dental, vision, retirement },
//   }

import { num, strOrNull } from './censusFileParser';

// ---------- shared mappers ----------

const PAY_SCHEDULE_MAP = {
  weekly: 'weekly',
  'bi-weekly': 'biweekly',
  biweekly: 'biweekly',
  semimonthly: 'semimonthly',
  'semi-monthly': 'semimonthly',
  monthly: 'monthly',
  annual: 'annual',
  annually: 'annual',
};

const FILING_MAP = {
  m: 'married', married: 'married', mfj: 'married', mj: 'married',
  s: 'single', single: 'single',
  h: 'head', hoh: 'head', head: 'head', 'head of household': 'head',
  mfs: 'married', // closest engine value
  qss: 'married',
  e: null, exempt: null,
};

function mapFiling(raw) {
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim().toLowerCase();
  if (!v) return null;
  return FILING_MAP[v] !== undefined ? FILING_MAP[v] : null;
}

function mapPaySchedule(raw) {
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim().toLowerCase();
  if (!v) return null;
  return PAY_SCHEDULE_MAP[v] || null;
}

// Engine-facing pay frequency. The optimization engine in calculations.js
// treats `employee.payFrequency` as a NUMBER (52 / 26 / 24 / 12 / 1). If we
// emit the human-readable string ('biweekly') instead, then `salary / periods`
// becomes `salary / 'biweekly'` = NaN, every plan becomes non-viable, and the
// employee is incorrectly flagged "salary too low". Always convert.
const PAY_SCHEDULE_PERIODS = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
  annual: 1,
};
function paySchedToPeriods(raw) {
  const sched = mapPaySchedule(raw);
  if (!sched) return null;
  return PAY_SCHEDULE_PERIODS[sched] || null;
}

function normalizeState(raw) {
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim().toUpperCase();
  if (!v) return null;
  // Two-letter code expected. If full name like "New York", caller should
  // resolve via stateData -- we just upper-case the 2-letter form.
  if (v.length === 2) return v;
  // Common full-name -> code small map (extend on demand)
  const FULL_TO_CODE = {
    'NEW YORK': 'NY', CALIFORNIA: 'CA', TEXAS: 'TX', FLORIDA: 'FL',
    ILLINOIS: 'IL', PENNSYLVANIA: 'PA', OHIO: 'OH', GEORGIA: 'GA',
    'NORTH CAROLINA': 'NC', MICHIGAN: 'MI',
  };
  return FULL_TO_CODE[v] || v.slice(0, 2);
}

function pickFirst(row, headers) {
  for (const h of headers) {
    if (row[h] !== null && row[h] !== undefined && String(row[h]).trim() !== '') {
      return row[h];
    }
  }
  return null;
}

function dominantValue(values) {
  // Returns the single non-empty value if all rows agree, else null
  const set = new Set(values.filter((v) => v !== null && v !== undefined && String(v).trim() !== ''));
  if (set.size === 1) return [...set][0];
  return null;
}

// Phase 17: pull a city/zip from any row, probing common header variants.
const CITY_HEADERS = [
  'City', 'city', 'Home City', 'home city', 'Work City', 'work city',
  'Residence City', 'Mailing City', 'Address City', 'Employee City',
];
const ZIP_HEADERS = [
  'Zip', 'ZIP', 'zip', 'Zip Code', 'ZIP Code', 'zip code', 'Postal Code',
  'postal code', 'Home Zip', 'Mailing Zip', 'Mailing ZIP', 'Postal',
  'Home ZIP Code', 'Address Zip', 'Address ZIP',
];
function pickCity(row) {
  return strOrNull(pickFirst(row, CITY_HEADERS));
}
function pickZip(row) {
  const raw = pickFirst(row, ZIP_HEADERS);
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim();
  if (!v) return null;
  // Take first 5 digits to normalize ZIP+4 → ZIP
  const digits = v.replace(/\D/g, '');
  return digits ? digits.slice(0, 5) : null;
}

// ---------- per-format transformers ----------

function transformStandard(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    const first = pickFirst(row, ['First Name', 'first name']);
    const last = pickFirst(row, ['Last Name', 'last name']);
    const name = pickFirst(row, ['Name']) || [first, last].filter(Boolean).join(' ').trim();
    const salary = num(pickFirst(row, ['Annual Salary', 'Salary', 'annual salary', 'salary']));
    const filing = mapFiling(pickFirst(row, ['Filing Status', 'filing status']));
    const state = normalizeState(pickFirst(row, ['State', 'state']));
    return {
      employeeId: strOrNull(pickFirst(row, ['Employee ID', 'employee id', 'Employee Id'])) || `row-${idx + 1}`,
      name: name || null,
      firstName: strOrNull(first),
      lastName: strOrNull(last),
      email: strOrNull(pickFirst(row, ['Email', 'email'])),
      salary,
      annualSalary: salary,
      state,
      filing,
      filingStatus: filing,
      payFrequency: paySchedToPeriods(pickFirst(row, ['Pay Frequency', 'pay frequency'])),
      paySchedule: mapPaySchedule(pickFirst(row, ['Pay Frequency', 'pay frequency'])),
      hireDate: strOrNull(pickFirst(row, ['Hire Date', 'hire date'])),
      weeklyHours: num(pickFirst(row, ['Weekly Hours', 'weekly hours'])) || null,
      workerType: strOrNull(pickFirst(row, ['Worker Type', 'worker type'])),
      department: strOrNull(pickFirst(row, ['Department', 'department'])),
      filingStatusInferred: false,
      sourceFormat: 'standard',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: false,
      missingFieldsExtra: [],
    }),
  };
}

function transformHealthCues(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    const first = strOrNull(pickFirst(row, ['First Name', 'first name']));
    const last = strOrNull(pickFirst(row, ['Last Name', 'last name']));
    const name = [first, last].filter(Boolean).join(' ').trim() || null;
    const yearly = num(pickFirst(row, ['Yearly', 'yearly']));
    const grossPerPeriod = num(pickFirst(row, ['Gross Wages Per Pay Period', 'gross wages per pay period']));
    const paySched = mapPaySchedule(pickFirst(row, ['Pay Schedule', 'pay schedule']));
    const periods = paySched ? PAY_SCHEDULE_PERIODS[paySched] : null;
    // If yearly is missing but grossPerPeriod + paySched are present, derive
    let salary = yearly;
    if (!salary && grossPerPeriod && periods) {
      salary = grossPerPeriod * periods;
    }
    const filing = mapFiling(pickFirst(row, [
      'Federal Marital/Filing Status',
      'federal marital/filing status',
      'Federal Marital Filing Status',
    ]));
    return {
      employeeId: strOrNull(pickFirst(row, ['Employee Id', 'Employee ID', 'employee id'])) || `row-${idx + 1}`,
      name,
      firstName: first,
      lastName: last,
      email: strOrNull(pickFirst(row, ['Email', 'email'])),
      salary,
      annualSalary: salary,
      state: normalizeState(pickFirst(row, ['State', 'state'])),
      filing,
      filingStatus: filing,
      payFrequency: periods, // number for engine
      paySchedule: paySched, // string for UI/meta
      grossPerPeriod: grossPerPeriod || null,
      filingStatusInferred: false,
      sourceFormat: 'healthcues',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: false,
    }),
  };
}

const ADP_EARNINGS_CODES = [
  'REG', 'OT', 'OTP', 'HOL', 'NYSICK', 'SICK', 'WPPTO', 'PTO', 'VAC',
  'BO', 'INSERV', 'SOH', 'TT', 'MISC', 'REIM', 'MUTOT', 'MUTREG', 'RETBASE',
];

function transformAdp(sheet) {
  // ADP "Original Data" exports have TWO columns named "Total" (earnings total
  // and ER-tax total). The parser disambiguates duplicates by suffixing later
  // occurrences with "_2", "_3", ... so headers are now ['Total', 'Total_2'].
  // The earnings total is the FIRST one (located before the FUTAER column).
  const headerLower = sheet.headers.map((h) => h.toLowerCase());
  const futaIdx = headerLower.indexOf('futaer');
  // First "Total" = earnings total. We look for an exact 'total' match before
  // FUTAER; if not present, we'll just sum the earnings codes.
  let earningsTotalKey = null;
  for (let i = 0; i < sheet.headers.length; i++) {
    if (futaIdx >= 0 && i >= futaIdx) break;
    if (sheet.headers[i].toLowerCase() === 'total') {
      earningsTotalKey = sheet.headers[i];
      break;
    }
  }

  const employees = sheet.dataRows.map((row, idx) => {
    const empId = strOrNull(pickFirst(row, ['Employee Number', 'employee number', 'Employee Id', 'Employee ID', 'employee id'])) || `row-${idx + 1}`;
    // Sum all earnings codes (skip codes not present in the file)
    let computedTotal = 0;
    let regular = 0;
    let overtime = 0;
    ADP_EARNINGS_CODES.forEach((code) => {
      const v = num(row[code]);
      computedTotal += v;
      if (code === 'REG') regular = v;
      if (code === 'OT' || code === 'OTP') overtime += v;
    });
    // Prefer the explicit earnings-Total column when present
    const earningsTotal = earningsTotalKey ? num(row[earningsTotalKey]) : 0;
    const annualSalary = earningsTotal || computedTotal;
    const otRatio = annualSalary > 0 ? overtime / annualSalary : 0;
    const regularPct = annualSalary > 0 ? regular / annualSalary : 0;

    return {
      employeeId: empId,
      name: null, // ADP exports don't include names
      firstName: null,
      lastName: null,
      email: null,
      salary: annualSalary,
      annualSalary,
      state: null, // not in ADP earnings exports — must be confirmed
      filing: null,
      filingStatus: null,
      payFrequency: null,
      filingStatusInferred: false,
      sourceFormat: 'adp_export',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
      validation: {
        actualFutaEr: num(row['FUTAER']),
        actualMedicareEr: num(row['MEDIER']),
        actualSsEr: num(row['SSECER']),
        actualStateEr: num(row['NYSUIER']) + num(row['NYMTA']),
      },
      earnings: {
        regularPay: regular,
        overtimePay: overtime,
        otRatio,
        otFlag: otRatio > 0.4,
        regularPct,
        variableIncomeFlag: regularPct > 0 && regularPct < 0.6,
        totalEarnings: annualSalary,
      },
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: true,
      hasEarningsBreakdown: true,
    }),
  };
}

function transformPaycom(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    const empId = strOrNull(pickFirst(row, ['Employee Number', 'employee number', 'Employee #', 'Employee ID']));
    const reg = num(pickFirst(row, ['Regular', 'regular', 'Regular Pay']));
    const ot = num(pickFirst(row, ['Overtime', 'overtime', 'Overtime Pay']));
    const total = num(pickFirst(row, ['Total Pay', 'Total', 'YTD Gross', 'Gross']));
    const annualSalary = total || (reg + ot);
    const otRatio = annualSalary > 0 ? ot / annualSalary : 0;
    return {
      employeeId: empId || `row-${idx + 1}`,
      name: strOrNull(pickFirst(row, ['Name', 'Employee Name'])),
      firstName: strOrNull(pickFirst(row, ['First Name'])),
      lastName: strOrNull(pickFirst(row, ['Last Name'])),
      email: strOrNull(pickFirst(row, ['Email'])),
      salary: annualSalary,
      annualSalary,
      state: normalizeState(pickFirst(row, ['State'])),
      filing: mapFiling(pickFirst(row, ['Filing Status', 'Marital Status'])),
      filingStatus: mapFiling(pickFirst(row, ['Filing Status', 'Marital Status'])),
      payFrequency: null,
      filingStatusInferred: false,
      sourceFormat: 'paycom_export',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
      earnings: {
        regularPay: reg, overtimePay: ot, otRatio,
        otFlag: otRatio > 0.4,
        regularPct: annualSalary > 0 ? reg / annualSalary : 0,
        variableIncomeFlag: false, totalEarnings: annualSalary,
      },
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: true,
    }),
  };
}

function transformPaycor(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    const empId = strOrNull(pickFirst(row, ['Employee Number', 'Employee ID']));
    const reg = num(pickFirst(row, ['REG', 'Reg']));
    const ot = num(pickFirst(row, ['OT', 'Ot']));
    const pto = num(pickFirst(row, ['PTO']));
    const total = num(pickFirst(row, ['Total Pay', 'Total', 'YTD'])) || reg + ot + pto;
    const otRatio = total > 0 ? ot / total : 0;
    return {
      employeeId: empId || `row-${idx + 1}`,
      name: strOrNull(pickFirst(row, ['Name', 'Employee Name'])),
      firstName: strOrNull(pickFirst(row, ['First Name'])),
      lastName: strOrNull(pickFirst(row, ['Last Name'])),
      email: strOrNull(pickFirst(row, ['Email'])),
      salary: total, annualSalary: total,
      state: normalizeState(pickFirst(row, ['State'])),
      filing: mapFiling(pickFirst(row, ['Filing Status'])),
      filingStatus: mapFiling(pickFirst(row, ['Filing Status'])),
      filingStatusInferred: false,
      sourceFormat: 'paycor_export',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
      earnings: {
        regularPay: reg, overtimePay: ot, otRatio,
        otFlag: otRatio > 0.4,
        regularPct: total > 0 ? reg / total : 0,
        variableIncomeFlag: false, totalEarnings: total,
      },
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: true,
    }),
  };
}

function transformGusto(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    const reg = num(pickFirst(row, ['regular_hours', 'Regular Hours'])); // hours
    const regPay = num(pickFirst(row, ['regular_pay', 'Regular Pay']));
    const otPay = num(pickFirst(row, ['overtime_pay', 'Overtime Pay']));
    const total = num(pickFirst(row, ['total_pay', 'Total Pay', 'YTD']));
    const annualSalary = total || (regPay + otPay);
    const otRatio = annualSalary > 0 ? otPay / annualSalary : 0;
    return {
      employeeId: strOrNull(pickFirst(row, ['employee_id', 'Employee ID'])) || `row-${idx + 1}`,
      name: strOrNull(pickFirst(row, ['employee_name', 'Name'])),
      firstName: strOrNull(pickFirst(row, ['first_name', 'First Name'])),
      lastName: strOrNull(pickFirst(row, ['last_name', 'Last Name'])),
      email: strOrNull(pickFirst(row, ['email', 'Email'])),
      salary: annualSalary, annualSalary,
      state: normalizeState(pickFirst(row, ['state', 'State'])),
      filing: mapFiling(pickFirst(row, ['filing_status', 'Filing Status'])),
      filingStatus: mapFiling(pickFirst(row, ['filing_status', 'Filing Status'])),
      filingStatusInferred: false,
      sourceFormat: 'gusto_export',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
      weeklyHours: reg / 52 || null,
      earnings: {
        regularPay: regPay, overtimePay: otPay, otRatio,
        otFlag: otRatio > 0.4,
        regularPct: annualSalary > 0 ? regPay / annualSalary : 0,
        variableIncomeFlag: false, totalEarnings: annualSalary,
      },
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: true,
    }),
  };
}

function transformGeneric(sheet) {
  const employees = sheet.dataRows.map((row, idx) => {
    // Try a wide range of common header names
    const name = pickFirst(row, ['Name', 'Employee Name', 'Full Name']);
    const first = strOrNull(pickFirst(row, ['First Name', 'first name', 'First', 'FName']));
    const last = strOrNull(pickFirst(row, ['Last Name', 'last name', 'Last', 'LName']));
    const composedName = name || [first, last].filter(Boolean).join(' ').trim() || null;
    const salary = num(pickFirst(row, [
      'Annual Salary', 'Salary', 'Yearly', 'Annual Pay', 'Annual Wages',
      'Gross Pay', 'Gross Wages', 'Income', 'Compensation',
    ]));
    const filing = mapFiling(pickFirst(row, [
      'Filing Status', 'Marital Status', 'Federal Marital/Filing Status',
    ]));
    return {
      employeeId: strOrNull(pickFirst(row, ['Employee ID', 'Employee Id', 'employee id', 'Employee Number'])) || `row-${idx + 1}`,
      name: composedName,
      firstName: first,
      lastName: last,
      email: strOrNull(pickFirst(row, ['Email', 'email', 'Work Email'])),
      salary, annualSalary: salary,
      state: normalizeState(pickFirst(row, ['State', 'state'])),
      filing, filingStatus: filing,
      payFrequency: paySchedToPeriods(pickFirst(row, ['Pay Frequency', 'Pay Schedule'])),
      paySchedule: mapPaySchedule(pickFirst(row, ['Pay Frequency', 'Pay Schedule'])),
      filingStatusInferred: false,
      sourceFormat: 'generic',
      sourceSheet: sheet.name,
      city: pickCity(row),
      zip: pickZip(row),
    };
  });
  return {
    employees,
    meta: buildMetaFromEmployees(employees, sheet, {
      hasEmployerTaxColumns: false,
      hasEarningsBreakdown: false,
    }),
  };
}

// ---------- meta builder + existing-deduction detection ----------

function detectExistingDeductions(rows, headers) {
  const candidates = {
    majorMedical: ['Major Medical', 'Medical', 'Health Premium', 'Medical Premium', 'Employee Contribution Major Medical'],
    dental: ['Dental', 'Dental Premium', 'Employee Contribution Dental'],
    vision: ['Vision', 'Vision Premium', 'Employee Contribution Vision'],
    retirement: ['401K', '401(k)', '401k', 'Retirement', 'Retirement Contribution'],
  };
  const result = {};
  for (const [key, names] of Object.entries(candidates)) {
    const matchedHeader = names.find((n) =>
      headers.some((h) => (h || '').toLowerCase() === n.toLowerCase())
    );
    if (!matchedHeader) {
      result[key] = { count: 0, average: 0, present: false };
      continue;
    }
    const realHeader = headers.find((h) => (h || '').toLowerCase() === matchedHeader.toLowerCase());
    let count = 0;
    let sum = 0;
    rows.forEach((row) => {
      const v = num(row[realHeader]);
      if (v > 0) {
        count += 1;
        sum += v;
      }
    });
    result[key] = {
      count, average: count > 0 ? sum / count : 0,
      present: count > 0,
      sourceColumn: realHeader,
    };
  }
  return result;
}

function buildMetaFromEmployees(employees, sheet, base) {
  const states = employees.map((e) => e.state);
  const detectedSingleState = dominantValue(states);
  // Use the human-readable string `paySchedule` field if present (set by
  // transformers that map a "Pay Schedule" column). Falls back to inferring
  // from the numeric `payFrequency` so the UI hint still says "Bi-Weekly"
  // instead of "26".
  const PERIODS_TO_NAME = { 52: 'weekly', 26: 'biweekly', 24: 'semimonthly', 12: 'monthly', 1: 'annual' };
  const detectedPaySchedule = dominantValue(employees.map((e) => e.paySchedule || PERIODS_TO_NAME[e.payFrequency] || null));
  const missingFields = [];
  if (employees.some((e) => !e.filing)) missingFields.push('filing');
  if (employees.some((e) => !e.state)) missingFields.push('state');
  if (employees.some((e) => !e.email)) missingFields.push('email');
  if (employees.some((e) => !e.name && !e.firstName && !e.lastName)) missingFields.push('name');
  if (employees.some((e) => !e.payFrequency)) missingFields.push('payFrequency');

  const existingDeductions = detectExistingDeductions(sheet.dataRows, sheet.headers);

  return {
    sourceFormat: employees[0]?.sourceFormat || 'unknown',
    sourceSheet: sheet.name,
    headerRowIndex: sheet.headerRowIndex,
    detectedPaySchedule,
    detectedSingleState,
    missingFields: [...new Set(missingFields)],
    existingDeductions,
    rowCount: sheet.meta.dataRowCount,
    headers: sheet.headers,
    totalsRow: sheet.totalsRow || null,
    ...base,
  };
}

// ---------- registry ----------

export const FORMAT_TRANSFORMERS = {
  standard: { name: 'HealthCues Engine standard template', transform: transformStandard },
  healthcues: { name: 'HealthCues partner census template', transform: transformHealthCues },
  adp_export: { name: 'ADP Workforce Now export', transform: transformAdp },
  paycom_export: { name: 'Paycom export', transform: transformPaycom },
  paycor_export: { name: 'Paycor export', transform: transformPaycor },
  gusto_export: { name: 'Gusto export', transform: transformGusto },
  generic: { name: 'Generic spreadsheet (best-effort)', transform: transformGeneric },
};

export function transformSheet(sheet, formatKey) {
  const t = FORMAT_TRANSFORMERS[formatKey] || FORMAT_TRANSFORMERS.generic;
  return t.transform(sheet);
}
